import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import CreateFirmForm from '../components/CreateFirmForm';
import CreateUserForm from '../components/CreateUserForm';
import UsersTable from '../components/UsersTable';
import ResetPasswordModal from '../components/ResetPasswordModal';

import firmService from '../services/firmService';
import userService from '../services/userService';
import '../styles/firms.css';
import '../styles/users.css';

const getErrorMessage = (error) =>
  error.response?.data?.message || error.message || 'Something went wrong.';

const FirmDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [firm, setFirm] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(false);
  const [creatingUser, setCreatingUser] = useState(false);
  const [resetUser, setResetUser] = useState(null);

  const loadDetails = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [firmResponse, usersResponse] = await Promise.all([
        firmService.getFirmById(id),
        userService.getUsers({ firm_id: id, role: 'distributor' }),
      ]);

      const firmData = firmResponse.data?.firm;

      if (!firmData) {
        throw new Error('Firm details were not returned by the server.');
      }

      setFirm(firmData);
      setUsers(usersResponse.data?.users || []);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const handleUpdateFirm = async (payload) => {
    setSaving(true);
    setError('');

    try {
      await firmService.updateFirm(id, payload);
      setEditing(false);
      await loadDetails();
    } catch (err) {
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async (payload) => {
    setSaving(true);
    setError('');

    try {
      await userService.createUser({
        ...payload,
        firm_id: String(id),
      });

      setCreatingUser(false);
      await loadDetails();
    } catch (err) {
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleUserStatusChange = async (user, isActive) => {
    const action = isActive ? 'activate' : 'deactivate';

    if (!window.confirm(`Are you sure you want to ${action} ${user.username}?`)) {
      return;
    }

    setError('');

    try {
      await userService.updateUserStatus(user.id, isActive);
      await loadDetails();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleResetPassword = async (user, password) => {
    setSaving(true);
    setError('');

    try {
      await userService.resetUserPassword(user.id, password);
      setResetUser(null);
    } catch (err) {
      setError(getErrorMessage(err));
      throw err;
    } finally {
      setSaving(false);
    }
  };

  const handleFirmStatusChange = async () => {
    if (!firm) return;

    const nextStatus = firm.status === 'active' ? 'inactive' : 'active';

    if (!window.confirm(`Change this firm's status to ${nextStatus}?`)) {
      return;
    }

    setError('');

    try {
      await firmService.updateFirmStatus(id, nextStatus);
      await loadDetails();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  if (loading) {
    return <main className="admin-page">Loading firm details...</main>;
  }

  if (!firm) {
    return (
      <main className="admin-page">
        <h1>Firm not found</h1>
        {error && <div className="admin-alert admin-alert-error">{error}</div>}
        <button
          type="button"
          className="admin-button admin-button-secondary"
          onClick={() => navigate('/admin/firms')}
        >
          Back to Firms
        </button>
      </main>
    );
  }

  return (
    <main className="admin-page">
      <header className="admin-page-header">
        <div>
          <p className="admin-eyebrow">FIRM DETAILS</p>
          <h1>{firm.firm_name}</h1>
          <p>
            Firm code: {firm.firm_code} · ID: {firm.id}
          </p>
        </div>

        <div className="admin-header-actions">
          <Link
            to="/admin/firms"
            className="admin-button admin-button-secondary"
          >
            Back to Firms
          </Link>

          <button
            type="button"
            className="admin-button admin-button-primary"
            onClick={() => setEditing((previous) => !previous)}
          >
            {editing ? 'Cancel Editing' : 'Edit Firm'}
          </button>
        </div>
      </header>

      {error && (
        <div className="admin-alert admin-alert-error" role="alert">
          {error}
        </div>
      )}

      {editing ? (
        <section className="admin-section">
          <CreateFirmForm
            key={firm.id}
            initialValues={firm}
            onSubmit={handleUpdateFirm}
            onCancel={() => setEditing(false)}
            submitting={saving}
            submitLabel="Save Changes"
          />
        </section>
      ) : (
        <section className="admin-section">
          <div className="admin-section-header">
            <div>
              <h2>Firm Information</h2>
              <p>Business and contact details.</p>
            </div>

            <span
              className={`admin-status ${
                firm.status === 'active' ? 'active' : 'inactive'
              }`}
            >
              {firm.status}
            </span>
          </div>

          <div className="firm-details-grid">
            {[
              ['Owner', firm.owner_name],
              ['Mobile', firm.mobile],
              ['Email', firm.email],
              ['Address', firm.address],
              ['GST Number', firm.gst_number],
              ['PAN Number', firm.pan_number],
              ['Bank Name', firm.bank_name],
              ['Account Holder', firm.account_holder_name],
              ['Account Number', firm.account_number],
              ['IFSC Code', firm.ifsc_code],
              [
                'Created At',
                firm.created_at
                  ? new Date(firm.created_at).toLocaleDateString()
                  : null,
              ],
            ].map(([label, value]) => (
              <div className="firm-detail-item" key={label}>
                <span>{label}</span>
                <strong>{value || '—'}</strong>
              </div>
            ))}
          </div>

          <div className="admin-form-actions">
            <button
              type="button"
              className={`admin-button ${
                firm.status === 'active'
                  ? 'admin-button-danger'
                  : 'admin-button-success'
              }`}
              onClick={handleFirmStatusChange}
            >
              {firm.status === 'active' ? 'Deactivate Firm' : 'Activate Firm'}
            </button>
          </div>
        </section>
      )}

      <section className="admin-section">
        <div className="admin-section-header">
          <div>
            <h2>Distributor Users</h2>
            <p>{users.length} users associated with this firm.</p>
          </div>

          <button
            type="button"
            className="admin-button admin-button-primary"
            onClick={() => setCreatingUser((previous) => !previous)}
          >
            {creatingUser ? 'Cancel' : '+ Create User'}
          </button>
        </div>

        {creatingUser && (
          <CreateUserForm
            firms={firm.status === 'active' ? [firm] : []}
            initialFirmId={firm.id}
            onSubmit={handleCreateUser}
            onCancel={() => setCreatingUser(false)}
            submitting={saving}
          />
        )}

        <UsersTable
          users={users}
          onStatusChange={handleUserStatusChange}
          onResetPassword={setResetUser}
        />
      </section>

      <ResetPasswordModal
        user={resetUser}
        isOpen={Boolean(resetUser)}
        onClose={() => setResetUser(null)}
        onSubmit={handleResetPassword}
        submitting={saving}
      />
    </main>
  );
};

export default FirmDetailsPage;